import React, { useState, useEffect } from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TableSortLabel,
  Paper,
  Typography,
  Chip,
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Visibility,
  Edit,
  Cancel,
  CheckCircle,
  CheckCircleOutline,
  PendingActions,
  UploadFile
} from '@mui/icons-material';
import { getStatusColor, getPriorityColor } from '../utils/maintenanceUtils';
import { getWorkOrderDisplayNumber } from '../../../utils/workOrderDisplayNumber';
import { getMaintenanceWorkOrderTypeLabel as getTypeLabel } from '../../../constants/maintenanceWorkOrderTypes';

const SORTABLE_COLUMNS = [
  { id: 'workOrder', label: 'Work Order #' },
  { id: 'aircraft', label: 'Aircraft' },
  { id: 'type', label: 'Type' },
  { id: 'title', label: 'Title' },
  { id: 'priority', label: 'Priority' },
  { id: 'status', label: 'Status' },
  { id: 'scheduledDate', label: 'Scheduled Date' }
];

function getComparator(order, orderBy) {
  return (a, b) => {
    const getValue = (item, col) => {
      switch (col) {
        case 'workOrder':
          return getWorkOrderDisplayNumber(item);
        case 'aircraft':
          return (item.aircraftId?.registration || 'Unknown').toLowerCase();
        case 'type':
          return (item.type || '').toLowerCase();
        case 'title':
          return (item.title || '').toLowerCase();
        case 'priority':
          return (item.priority || '').toLowerCase();
        case 'status':
          return (item.status || '').toLowerCase();
        case 'scheduledDate':
          return item.scheduledDate ? new Date(item.scheduledDate).getTime() : 0;
        default:
          return '';
      }
    };
    const aVal = getValue(a, orderBy);
    const bVal = getValue(b, orderBy);
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return order === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
    return order === 'asc' ? cmp : -cmp;
  };
}

/**
 * Table component for displaying maintenance work orders
 */
const MaintenanceTable = ({
  filteredMaintenance,
  searchTerm,
  statusFilter,
  priorityFilter,
  typeFilter,
  canManageMaintenance,
  canEditWorkOrder,
  canCancelWorkOrder,
  canAcknowledge,
  canPlan,
  canStoreAction,
  canExternalAgencyAction,
  canMROAction,
  canMROEngineerComplete,
  canMROQMCompletionAck,
  canCAMOQMCompletionAck,
  handleViewDetails,
  handleEdit,
  handleCancel,
  handleWorkflowAction
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('scheduledDate');
  const [order, setOrder] = useState('desc');

  // Reset to first page when filters or filtered list length changes
  useEffect(() => {
    setPage(0);
  }, [filteredMaintenance.length, searchTerm, statusFilter, priorityFilter, typeFilter]);

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const sortedMaintenance = React.useMemo(() => {
    return [...filteredMaintenance].sort(getComparator(order, orderBy));
  }, [filteredMaintenance, order, orderBy]);

  const paginatedMaintenance = sortedMaintenance.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {SORTABLE_COLUMNS.map((col) => (
              <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}>
                <TableSortLabel
                  active={orderBy === col.id}
                  direction={orderBy === col.id ? order : 'asc'}
                  onClick={(e) => handleRequestSort(e, col.id)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredMaintenance.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || statusFilter !== 'ALL' || priorityFilter !== 'ALL' || typeFilter !== 'ALL'
                    ? 'No work orders match the current filters'
                    : 'No work orders found'}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedMaintenance.map((item) => (
              <TableRow key={item._id}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                    WO #{getWorkOrderDisplayNumber(item) || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {item.aircraftId?.registration || 'Unknown'}
                </TableCell>
                <TableCell>{getTypeLabel(item.type)}</TableCell>
                <TableCell>{item.title}</TableCell>
                <TableCell>
                  <Chip
                    label={item.priority}
                    color={getPriorityColor(item.priority)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Chip
                      label={item.status}
                      color={getStatusColor(item.status)}
                      size="small"
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  {item.scheduledDate ? new Date(item.scheduledDate).toLocaleDateString() : 'Not scheduled'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(item)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    {canEditWorkOrder?.(item) && (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(item)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canCancelWorkOrder?.(item) && (
                      <Tooltip title="Cancel Work Order">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleCancel(item)}
                        >
                          <Cancel />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canAcknowledge(item) && (
                      <Tooltip title="CAMO_QM Acknowledge">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleWorkflowAction('camoQmAck', item)}
                        >
                          <CheckCircleOutline />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canPlan(item) && (
                      <Tooltip title="Planning Approval">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleWorkflowAction('planning', item)}
                        >
                          <PendingActions />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canStoreAction(item) && (
                      <Tooltip title="Store Action">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleWorkflowAction('store', item)}
                        >
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canExternalAgencyAction?.(item) && (
                      <Tooltip title="External Agency Documents">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleWorkflowAction('externalAgency', item)}
                        >
                          <UploadFile />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canMROAction(item) && (
                      <Tooltip title="MRO Action">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleWorkflowAction('mro', item)}
                        >
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canMROEngineerComplete(item) && (
                      <Tooltip title="MRO_ENGINEER Complete">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleWorkflowAction('mroEngineer', item)}
                        >
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canMROQMCompletionAck(item) && (
                      <Tooltip title="MRO QM Acknowledge Completion">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleWorkflowAction('mroQmCompletion', item)}
                        >
                          <CheckCircleOutline />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canCAMOQMCompletionAck(item) && (
                      <Tooltip title="CAMO_QM Acknowledge Completion">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleWorkflowAction('camoQmCompletion', item)}
                        >
                          <CheckCircleOutline />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {filteredMaintenance.length > 0 && (
        <TablePagination
          component="div"
          count={sortedMaintenance.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`}
        />
      )}
    </TableContainer>
  );
};

export default MaintenanceTable;
